/**
 * Weight Ticket Data Hooks
 * React Query hooks for fetching and managing weight ticket data
 *
 * Following DDD Principles:
 * - Hooks consume application services
 * - React Query handles caching and state management
 * - Components receive view models ready for display
 * - NO business logic in hooks
 */

import { useQuery } from '@tanstack/react-query';
import { weightTicketApplicationService } from '../WeightTicketApplicationService';
import type {
  TicketListViewModel,
  TicketDetailViewModel,
  PendingReviewViewModel,
  TicketReportViewModel,
} from '../view-models/WeightTicketViewModel';
import type { TicketFilters, TicketReportParams } from '@/services/service-provider.service';

/**
 * Weight Ticket query key factory
 * Provides consistent cache keys for React Query
 */
export const ticketKeys = {
  all: ['weight-tickets'] as const,
  lists: () => [...ticketKeys.all, 'list'] as const,
  list: (filters?: TicketFilters) => [...ticketKeys.lists(), { filters }] as const,
  details: () => [...ticketKeys.all, 'detail'] as const,
  detail: (id: string) => [...ticketKeys.details(), id] as const,
  pendingReview: () => [...ticketKeys.all, 'pending-review'] as const,
  reports: () => [...ticketKeys.all, 'report'] as const,
  report: (params: TicketReportParams) => [...ticketKeys.reports(), params] as const,
};

/**
 * Hook to fetch ticket list with optional filters
 */
export function useTicketList(filters?: TicketFilters) {
  return useQuery<TicketListViewModel, Error>({
    queryKey: ticketKeys.list(filters),
    queryFn: () => weightTicketApplicationService.getTicketList(filters),
    staleTime: 1000 * 60 * 1, // Consider data fresh for 1 minute
    gcTime: 1000 * 60 * 5, // Keep in cache for 5 minutes
    refetchOnWindowFocus: true,
    retry: 2,
  });
}

/**
 * Hook to fetch single ticket detail
 */
export function useTicketDetail(ticketId: string | undefined) {
  return useQuery<TicketDetailViewModel, Error>({
    queryKey: ticketKeys.detail(ticketId ?? ''),
    queryFn: () => {
      if (!ticketId) {
        throw new Error('Ticket ID is required');
      }
      return weightTicketApplicationService.getTicketDetail(ticketId);
    },
    enabled: !!ticketId, // Only run query if ticketId exists
    staleTime: 1000 * 60 * 5, // Consider data fresh for 5 minutes
    gcTime: 1000 * 60 * 10, // Keep in cache for 10 minutes
    retry: 2,
  });
}

/**
 * Hook to fetch tickets pending review
 */
export function usePendingReview() {
  return useQuery<PendingReviewViewModel, Error>({
    queryKey: ticketKeys.pendingReview(),
    queryFn: () => weightTicketApplicationService.getPendingReview(),
    staleTime: 1000 * 30, // Consider data fresh for 30 seconds
    gcTime: 1000 * 60 * 5, // Keep in cache for 5 minutes
    refetchOnWindowFocus: true,
    refetchInterval: 1000 * 60, // Auto-refetch every minute
    retry: 2,
  });
}

/**
 * Hook to fetch ticket report
 */
export function useTicketReport(params: TicketReportParams) {
  return useQuery<TicketReportViewModel, Error>({
    queryKey: ticketKeys.report(params),
    queryFn: () => weightTicketApplicationService.getTicketReport(params),
    staleTime: 1000 * 60 * 5, // Consider data fresh for 5 minutes
    gcTime: 1000 * 60 * 10, // Keep in cache for 10 minutes
    retry: 2,
  });
}

/**
 * Hook to get all tickets (no filters)
 * Useful for dashboard overview
 */
export function useAllTickets() {
  return useTicketList();
}

/**
 * Hook to get pending tickets only
 */
export function usePendingTickets() {
  return useTicketList({ status: 'pending' });
}

/**
 * Hook to get approved tickets only
 */
export function useApprovedTickets() {
  return useTicketList({ status: 'approved' });
}

/**
 * Hook to get rejected tickets only
 */
export function useRejectedTickets() {
  return useTicketList({ status: 'rejected' });
}
